import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ISchedulerData } from '@interfaces/IScheduler';
import type { ICommittedActivity } from '@apis/activities';
import { OPEN_ACTIVITY_PARAM } from '@constants/activitiesParams';
import { useSetActiveSchedule } from '@hooks/useActiveSchedule';
import { useSchedulerActions } from '../../SchedulerPage/hooks/useSchedulerStore';
import { usePanelUrlSync } from '../../SchedulerPage/hooks/usePanelUrlSync';
import type { ICommittedActivitiesState } from './useCommittedActivities';

/**
 * Opens the shared Scheduler TaskPanel drawer on a task, Overview tab — what
 * clicking an activity does in every view of this page. ActivitiesPage
 * bootstraps the scheduler store and mounts the panel; this only points it.
 */
export function useOpenTaskPanel(): (taskId: string) => void {
  const { openPanel, setSelectedTask } = useSchedulerActions();
  return useCallback(
    (taskId: string) => {
      setSelectedTask(taskId);
      openPanel({ mode: 'edit', taskId, initialTab: 'overview' });
    },
    [openPanel, setSelectedTask],
  );
}

interface IUseActivityUrlSyncArgs {
  /** The page's own schedule's tasks — undefined until it loads. Links resolve
   *  against these, never the store's copy, which may still be the schedule
   *  the Scheduler last showed. */
  tasks: ISchedulerData['tasks'] | undefined;
  /** A link's filters are still to leave the URL (`useLinkedFilters`). */
  paused: boolean;
}

/** `?openActivity=<taskId>&tab=<tab>` ⇄ the TaskPanel, as the Scheduler's
 *  `openTask` (`usePanelUrlSync`) — so every way an activity opens, from the
 *  list, the Work Plan or the tracker, leaves a shareable link behind. */
export function useActivityUrlSync({ tasks, paused }: IUseActivityUrlSyncArgs): void {
  usePanelUrlSync({ taskParam: OPEN_ACTIVITY_PARAM, tasks, paused });
}

/**
 * The schedule holding the activity a link names, looked up in the site-wide
 * committed feed. A copied programme can hold the same id twice; the schedule
 * on screen wins.
 */
export function scheduleOfLinkedActivity(
  taskId: string | null,
  activities: readonly ICommittedActivity[],
  onScreen: string | undefined,
): string | undefined {
  if (taskId === null) return undefined;
  const rows = activities.filter((row) => row.task.id === taskId);
  return (rows.find((row) => row.scheduleNumber === onScreen) ?? rows[0])?.scheduleNumber;
}

interface IUseLinkedActivityScheduleArgs {
  siteId: string;
  /** The site-wide committed feed — a link is not judged before it has answered. */
  feed: Pick<ICommittedActivitiesState, 'activities' | 'hasData'>;
  /** The schedule the page shows. */
  onScreen: string | undefined;
}

/**
 * `?openActivity=<id>` may name a promise from another of the site's schedules
 * (a chat mention, a pasted link); the page shows one at a time, so it moves
 * there first. True while the move is still ahead — the URL sync waits on it,
 * or it would judge the link against the schedule on screen and clear it.
 */
export function useLinkedActivitySchedule({ siteId, feed, onScreen }: IUseLinkedActivityScheduleArgs): boolean {
  const [params] = useSearchParams();
  const taskId = params.get(OPEN_ACTIVITY_PARAM);
  const setActiveSchedule = useSetActiveSchedule();
  const target = feed.hasData ? scheduleOfLinkedActivity(taskId, feed.activities, onScreen) : undefined;
  const moving = target !== undefined && target !== onScreen;
  useEffect(() => {
    if (target !== undefined && target !== onScreen) setActiveSchedule(siteId, target);
  }, [target, onScreen, siteId, setActiveSchedule]);
  return taskId !== null && (!feed.hasData || moving);
}
